import { useMemo } from 'react';
import type { RepairPartViewModel } from '@api/enterprise/openapi';

type FilteredPartsResult = {
  groups: Record<string, RepairPartViewModel[]>;
  flatList: RepairPartViewModel[];
  groupCounts: number[];
  companies: string[];
};

type UsePartsFilterParams = {
  data?: { value?: RepairPartViewModel[] } | RepairPartViewModel[];
  existingPartsSet: Set<string>;
  searchTerm: string;
  expandedGroups: Set<string>;
};

export const usePartsFilter = ({
  data,
  existingPartsSet,
  searchTerm,
  expandedGroups
}: UsePartsFilterParams): FilteredPartsResult => {
  return useMemo(() => {
    const parts = Array.isArray(data) ? data : data?.value || [];
    if (parts.length === 0) {
      return { groups: {}, flatList: [], groupCounts: [], companies: [] };
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const groups: Record<string, RepairPartViewModel[]> = {};
    const flatList: RepairPartViewModel[] = [];
    const groupCounts: number[] = [];
    const companies: string[] = [];

    parts.forEach(part => {
      const partKey = `${part.companyName}:${part.partNumber}`;
      if (existingPartsSet.has(partKey)) {
        return;
      }

      if (searchLower) {
        const matchesSearch =
          part.partNumber?.toLowerCase().includes(searchLower) ||
          part.description?.toLowerCase().includes(searchLower) ||
          part.companyName?.toLowerCase().includes(searchLower);

        if (!matchesSearch) {
          return;
        }
      }

      const company = part.companyName || 'Unknown';
      if (!groups[company]) {
        groups[company] = [];
        companies.push(company);
      }
      groups[company].push(part);
    });

    companies.sort((a, b) => a.localeCompare(b));

    const visibleCompanies: string[] = [];

    companies.forEach(company => {
      const parts = groups[company];
      const isExpanded = expandedGroups.has(company);

      visibleCompanies.push(company);
      groupCounts.push(isExpanded ? parts.length : 0);

      if (isExpanded) {
        parts.forEach(part => flatList.push(part));
      }
    });

    return { groups, flatList, groupCounts, companies: visibleCompanies };
  }, [data, existingPartsSet, searchTerm, expandedGroups]);
};
